<template>
  <Aggregate
    title="Markets"
    v-model="computedValue"
    :ItemConstructor="CaseModel.MarketProject"
    :headers="CaseModel.MarketProject.HEADERS"
    :cardProps="{ flat: true, class: [] }"
    toolbar-dense
    @change="$emit('change')"
  >
    <template #default="{ item, itemIndex }">
      <v-card flat class="mb-3">
        <v-card-text class="px-0">
          <InputGeo
            v-model="item.coordinates"
            label="Coordinates"
            map-height="150"
            nullable
            switch-text="Use Market coordinates"
            @change="$emit('change')"
          />
          <v-expansion-panels v-model="panelState" tile flat class="mx-0 px-0">
            <v-expansion-panel :key="0">
              <v-expansion-panel-header
                :class="`subtitle-1 font-weight-medium ${isEnabled(item.energy)}`"
              >
                <v-row no-gutters>
                  <v-col class="shrink mr-4">
                    <v-switch
                      v-model="item.energyEnabled"
                      dense
                      class="my-0 pt-0"
                      :height="25"
                      @click.native.stop=""
                      @change="toggleItem($event, itemIndex, 'energy')"
                    />
                  </v-col>
                  <v-col> Energy </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <SystemProjectSubMarket
                  v-model="item.energy"
                  :num_years="num_years"
                  :year_start="year_start"
                  :errors="errors"
                  :project-coordinates="projectCoordinates"
                  toggleable
                  :open="panelState === 0"
                  @change="$emit('change')"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel key="ppa">
              <v-expansion-panel-header
                :class="`subtitle-1 font-weight-medium ${isEnabled(item.ppa.active)}`"
                ><v-row no-gutters>
                  <v-col class="shrink mr-4">
                    <v-switch
                      v-model="item.ppa.active"
                      dense
                      class="my-0 pt-0"
                      :height="25"
                      @click.native.stop=""
                    />
                  </v-col>
                  <v-col> Power Purchase Agreement </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <InputYearlyProfile
                  v-if="item.ppa.active"
                  v-model="item.ppa.imports"
                  object-value
                  fill
                  toggle-items-type
                  label="imports PPA price"
                  hint="calculate the earnings from PPAs"
                  :num_years="num_years"
                  profile-type="PPA_ENERGY_PRICE"
                  @change="$emit('change')"
                ></InputYearlyProfile>
                <InputYearlyProfile
                  v-if="item.ppa.active"
                  v-model="item.ppa.exports"
                  object-value
                  fill
                  toggle-items-type
                  label="exports PPA price"
                  hint="calculate the earnings from PPAs"
                  :num_years="num_years"
                  profile-type="PPA_ENERGY_PRICE"
                  @change="$emit('change')"
                ></InputYearlyProfile>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel :key="2">
              <v-expansion-panel-header
                :class="`subtitle-1 font-weight-medium ${isEnabled(item.tariffs)}`"
                ><v-row no-gutters>
                  <v-col class="shrink mr-4">
                    <v-switch
                      v-model="item.tariffsEnabled"
                      dense
                      class="my-0 pt-0"
                      :height="25"
                      @click.native.stop=""
                      @change="toggleItem($event, itemIndex, 'tariffs')"
                    />
                  </v-col>
                  <v-col> Tariffs </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <SystemProjectSubMarket
                  v-model="item.tariffs"
                  :num_years="num_years"
                  :year_start="year_start"
                  :errors="errors"
                  :project-coordinates="projectCoordinates"
                  toggleable
                  :open="panelState === 2"
                  @change="$emit('change')"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel :key="3">
              <v-expansion-panel-header
                :class="`subtitle-1 font-weight-medium ${isEnabled(item.fuels)}`"
              >
                <v-row no-gutters>
                  <v-col class="shrink mr-4">
                    <v-switch
                      v-model="item.fuelsEnabled"
                      dense
                      class="my-0 pt-0"
                      :height="25"
                      @click.native.stop=""
                      @change="toggleItem($event, itemIndex, 'fuels')"
                    />
                  </v-col>
                  <v-col> Fuels </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <SystemProjectSubMarket
                  v-model="item.fuels"
                  :num_years="num_years"
                  :year_start="year_start"
                  :errors="errors"
                  toggleable
                  :open="panelState === 3"
                  @change="$emit('change')"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header
                :class="`subtitle-1 font-weight-medium ${isEnabled(item.reserves)}`"
              >
                Reserves
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <Aggregate
                  title="Reserves"
                  v-model="item.reserves"
                  :ItemConstructor="CaseModel.SubMarket"
                  :headers="[{ text: 'Reserve linked', value: 'project_reserve_id' }]"
                  :disabled="addReservesDisabled(item)"
                  @change="$emit('change')"
                >
                  <template #[`item.project_reserve_id`]="{ item: reserve }">
                    <v-select
                      v-model="reserve.project_reserve_id"
                      item-text="name"
                      item-value="id"
                      :items="availableReserves(item, reserve)"
                      @change="$emit('change')"
                    />
                  </template>
                  <template #default="{ item: reserve, errors }">
                    <SystemProjectSubMarket
                      v-model="item.reserves[reserve.id - 1]"
                      :num_years="num_years"
                      :year_start="year_start"
                      :errors="errors"
                      :key="item.id"
                      submarket-type="reserve"
                      :reserves="reserves"
                      @change="$emit('change')"
                    />
                  </template>
                </Aggregate>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </template>
  </Aggregate>
</template>

<script>
import { CaseModel } from "data-dictionary";

export default {
  name: "SystemProjectMarket",
  components: {
    Aggregate: () => import("@/components/Aggregate.vue"),
    InputGeo: () => import("@/components/InputGeo.vue"),
    SystemProjectSubMarket: () => import("@/components/SystemProjectSubMarket.vue"),
    InputYearlyProfile: () => import("@/components/InputYearlyProfile.vue"),
  },
  mixins: [],
  props: {
    value: { type: Array, required: true },
    num_years: { type: Number, required: true },
    year_start: { type: Number, required: true },
    reserves: { type: Array, required: true },
    errors: {
      type: Object,
      required: false,
    },
    projectCoordinates: { type: Array, required: false, default: undefined },
  },
  data() {
    return {
      CaseModel,
      panelState: null,
    };
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.$emit("change");
      },
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    addReservesDisabled(item) {
      return item.reserves.length >= this.reserves.length;
    },
    availableReserves(item, reserve) {
      console.debug(item, reserve);
      return [
        { name: "- Select Reserve -", id: null },
        ...this.reserves.filter(
          (resourceReserve) =>
            !item.reserves.map((r) => r.project_reserve_id).includes(resourceReserve.id)
        ),
        ...(reserve.project_reserve_id
          ? [this.reserves.find((r) => r.id === reserve.project_reserve_id)]
          : []),
      ];
    },
    isEnabled(item) {
      if (item) {
        if (Array.isArray(item)) {
          return !item.length ? "text--disabled" : "";
        }
        return "";
      }
      return "text--disabled";
    },
    toggleItem($event, index, submarket) {
      if ($event) {
        const obj = new this.CaseModel.SubMarket();
        this.computedValue[index - 1][submarket] = obj;
      } else {
        this.computedValue[index - 1][submarket] = null;
      }
    },
  },
};
</script>

<style></style>
